/**
 * Canonical unified post composer — UI matches ClubFeedCreatePostModal; submits via POST /api/v2/posts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { uploadClubFeedMedia } from "../../services/clubsApi";
import {
  createPost,
  normalizeFeedMediaUploadResponse,
  uploadForumPostFeedMedia,
} from "../../services/postsV2Api";
import { resolveClubMediaUrl } from "../comments/utils/clubFeedPostUtils";
import ClubFeedComposerMediaPanel from "../../components/Clubs/ClubFeedComposerMediaPanel";
import ClubFeedComposerTabList from "../../components/Clubs/ClubFeedComposerTabList";
import {
  ACCEPT_ATTR,
  ACCEPT_MIMES,
  MAX_CONTENT_CHARS,
  MAX_MEDIA_IMAGES_PER_POST,
  MAX_TITLE_CHARS,
  MAX_UPLOAD_BYTES,
  MIN_TITLE_CHARS,
  buildClubPostStoredContent,
  describeComposerBlockedReason,
  getUploadErrorMessage,
  isActiveMember,
  pickViewerId,
} from "../../components/Clubs/clubFeedCreatePostUtils";
import {
  clubFeedActionBase,
  clubPostCardBodyClass,
  clubPostCardBodyTextClass,
  clubPostCardHeaderClass,
  clubPostOptionsTriggerClass,
} from "../../components/Clubs/feedPostCardPresentation";
import {
  buildV2AttachmentPayloadFromComposer,
  buildV2CreatePostPayload,
  isReadyComposerAttachment,
} from "./unifiedPostComposerUtils";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

const fieldShellClass =
  "w-full rounded-lg border border-white/12 bg-[#121a26] px-3 py-2.5 text-[15px] text-white/[0.92] placeholder:text-white/30 outline-none transition duration-150 " +
  "hover:border-white/15 focus-visible:border-white/18 focus-visible:ring-1 focus-visible:ring-white/20 focus-visible:ring-offset-1 focus-visible:ring-offset-[#0b101a] disabled:opacity-45 disabled:pointer-events-none";

const alertInfoClass =
  "rounded-lg border border-white/10 bg-[#141c28] px-3 py-2.5 text-sm text-gray-400";

const alertErrorClass =
  "rounded-lg border border-rose-400/15 bg-rose-950/30 px-3 py-2.5 text-sm text-rose-100/80";

const BODY_ROWS = 8;
const BODY_MIN_PX = "min-h-[180px]";

function composerSlideSrc(att) {
  if (!att) return "";
  if (att.__localPreview) return att.__localPreview;
  const u = att.url ? String(att.url).trim() : "";
  if (!u) return "";
  return resolveClubMediaUrl(u) || u;
}

/**
 * @typedef {'community' | 'club'} UnifiedPostComposerSurface
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   surface: UnifiedPostComposerSurface,
 *   club?: { id?: number, club_id?: number, name?: string, slug?: string } | null,
 *   membership?: object | null,
 *   clubPickerOptions?: Array<{ id: number, name: string, slug?: string }>,
 *   defaultTargetClubId?: number | null,
 *   headlineTitle?: string,
 *   contextSubtitle?: string | null,
 *   onSuccess?: (result: { post: object, attachments: object[] }) => void | Promise<void>,
 * }} props
 */
export default function UnifiedPostComposer({
  isOpen,
  onClose,
  surface,
  club = null,
  membership = null,
  clubPickerOptions = [],
  defaultTargetClubId = null,
  headlineTitle,
  contextSubtitle,
  onSuccess,
}) {
  const { user } = useAuth();
  const [composerTab, setComposerTab] = useState("post");
  const [postTitle, setPostTitle] = useState("");
  const [postBody, setPostBody] = useState("");
  /** null = community-wide (no club); otherwise numeric club id for post + uploads. */
  const [selectedTargetClubId, setSelectedTargetClubId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [createPostBusy, setCreatePostBusy] = useState(false);
  const [createPostErr, setCreatePostErr] = useState("");
  const [infoNotice, setInfoNotice] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [mediaSlideIndex, setMediaSlideIndex] = useState(0);

  const attachmentInputRef = useRef(null);
  const replaceFileInputRef = useRef(null);
  const replaceSlideIndexRef = useRef(0);
  const titleInputRef = useRef(null);
  const bodyRef = useRef(null);
  const dragDepthRef = useRef(0);
  const composerTabRef = useRef(composerTab);
  const attachmentsRef = useRef(attachments);
  const uploadSeqRef = useRef(0);
  const readyCountRef = useRef(0);

  const isMediaTab = composerTab === "media";
  composerTabRef.current = composerTab;
  attachmentsRef.current = attachments;

  /** Clubs shown in the destination picker (current club merged in on club surface if missing). */
  const clubPickerOptionsEffective = useMemo(() => {
    const base = Array.isArray(clubPickerOptions) ? clubPickerOptions.filter(Boolean) : [];
    if (surface !== "club" || !club) return base;
    const cid = Number(club.club_id ?? club.id);
    if (!Number.isFinite(cid) || cid < 1) return base;
    if (base.some((c) => Number(c.id) === cid)) return base;
    return [
      {
        id: cid,
        name: String(club.name || "").trim() || "Club",
        slug: club.slug,
      },
      ...base,
    ];
  }, [clubPickerOptions, surface, club]);

  /** Destination club for the post and for media upload routing (null = community-wide, no club). */
  const effectivePostClubId = useMemo(() => {
    if (selectedTargetClubId == null) return null;
    const n = Number(selectedTargetClubId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedTargetClubId]);

  /** R2 upload routing: club path when a club is selected; else global forum path. */
  const effectiveUploadClubId = useMemo(() => {
    if (effectivePostClubId != null) return effectivePostClubId;
    return null;
  }, [effectivePostClubId]);

  const eligibleClubIds = useMemo(
    () => new Set(clubPickerOptionsEffective.map((c) => Number(c.id)).filter((id) => Number.isFinite(id) && id > 0)),
    [clubPickerOptionsEffective]
  );

  const clubMemberOk = useMemo(() => {
    if (surface === "community") {
      if (effectivePostClubId == null) return true;
      return eligibleClubIds.has(effectivePostClubId);
    }
    if (effectivePostClubId == null) return false;
    if (eligibleClubIds.size === 0) {
      const pageId = Number(club?.club_id ?? club?.id);
      return (
        isActiveMember(membership) &&
        Number.isFinite(pageId) &&
        pageId > 0 &&
        effectivePostClubId === pageId
      );
    }
    return eligibleClubIds.has(effectivePostClubId);
  }, [surface, effectivePostClubId, eligibleClubIds, membership, club]);

  const titleTrim = postTitle.trim();
  const titleLen = postTitle.length;
  const storedContent = isMediaTab
    ? titleTrim
    : buildClubPostStoredContent(postTitle, postBody);

  const titleOk =
    titleTrim.length >= MIN_TITLE_CHARS && titleTrim.length <= MAX_TITLE_CHARS;
  const hasReadyMedia = attachments.some(isReadyComposerAttachment);
  const contentLengthOk = storedContent.length <= MAX_CONTENT_CHARS;

  const canPublish = useMemo(() => {
    if (createPostBusy || uploading || attachments.some((a) => a.__uploading)) return false;
    if (!clubMemberOk) return false;
    if (!pickViewerId(user)) return false;
    if (surface === "club" && effectivePostClubId == null) return false;
    if (!titleOk || !contentLengthOk) return false;
    if (isMediaTab) {
      return hasReadyMedia;
    }
    return true;
  }, [
    createPostBusy,
    uploading,
    attachments,
    clubMemberOk,
    user,
    surface,
    effectivePostClubId,
    titleOk,
    contentLengthOk,
    isMediaTab,
    hasReadyMedia,
  ]);

  const footerHint = useMemo(() => {
    if (isMediaTab && uploading) return "Uploading…";
    const base = describeComposerBlockedReason({
      title: postTitle,
      contentLengthOk,
      hasMedia: hasReadyMedia,
      busy: createPostBusy,
      composerMode: isMediaTab ? "media" : "post",
    });
    if (base) return base;
    if (surface === "club" && !clubMemberOk) return "You need to be an active member to post.";
    return null;
  }, [
    isMediaTab,
    uploading,
    postTitle,
    contentLengthOk,
    hasReadyMedia,
    createPostBusy,
    surface,
    clubMemberOk,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setComposerTab("post");
    setPostTitle("");
    setPostBody("");
    const pageClubId = club ? Number(club.club_id ?? club.id) : null;
    const fromProp =
      defaultTargetClubId != null && Number.isFinite(Number(defaultTargetClubId))
        ? Number(defaultTargetClubId)
        : null;
    if (surface === "community") {
      setSelectedTargetClubId(null);
    } else if (fromProp != null && Number.isFinite(fromProp) && fromProp > 0) {
      setSelectedTargetClubId(fromProp);
    } else if (Number.isFinite(pageClubId) && pageClubId > 0) {
      setSelectedTargetClubId(pageClubId);
    } else {
      setSelectedTargetClubId(null);
    }
    setAttachments([]);
    setUploadError(null);
    setCreatePostErr("");
    setInfoNotice("");
    setDragActive(false);
    setMediaSlideIndex(0);
    readyCountRef.current = 0;
    const t = window.setTimeout(() => titleInputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [isOpen, surface, defaultTargetClubId, club]);

  useEffect(() => {
    if (!infoNotice) return;
    const timer = window.setTimeout(() => setInfoNotice(""), 3400);
    return () => window.clearTimeout(timer);
  }, [infoNotice]);

  useEffect(() => {
    const len = attachments.length;
    if (len === 0) {
      setMediaSlideIndex(0);
      return;
    }
    setMediaSlideIndex((i) => Math.min(i, len - 1));
  }, [attachments.length]);

  useEffect(() => {
    const ready = attachments.filter(isReadyComposerAttachment).length;
    if (ready > readyCountRef.current && attachments.length > 0) {
      setMediaSlideIndex(attachments.length - 1);
    }
    readyCountRef.current = ready;
  }, [attachments]);

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((a) => {
        const b = a?.__localPreview;
        if (b && String(b).startsWith("blob:")) {
          try {
            URL.revokeObjectURL(b);
          } catch {
            /* ignore */
          }
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (createPostBusy || uploading) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, createPostBusy, uploading, onClose]);

  const tryClose = useCallback(() => {
    if (createPostBusy || uploading) return;
    onClose();
  }, [createPostBusy, uploading, onClose]);

  const revokeLocalPreview = useCallback((row) => {
    const b = row?.__localPreview;
    if (b && String(b).startsWith("blob:")) {
      try {
        URL.revokeObjectURL(b);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const uploadImageFile = useCallback(
    async (file) => {
      const cid = effectiveUploadClubId;
      if (cid != null && Number.isFinite(cid)) {
        const res = await uploadClubFeedMedia({ clubId: cid, file });
        return normalizeFeedMediaUploadResponse(res);
      }
      const res = await uploadForumPostFeedMedia({ file });
      return normalizeFeedMediaUploadResponse(res);
    },
    [effectiveUploadClubId]
  );

  const processFilesBatch = useCallback(
    async (rawFiles) => {
      if (composerTabRef.current !== "media") {
        setComposerTab("media");
      }
      const incoming = Array.from(rawFiles || []).filter(
        (f) => f != null && typeof f.size === "number"
      );
      if (!incoming.length) return;

      const room = Math.max(0, MAX_MEDIA_IMAGES_PER_POST - attachmentsRef.current.length);
      if (room <= 0) {
        setInfoNotice(`You can add up to ${MAX_MEDIA_IMAGES_PER_POST} images per post.`);
        return;
      }

      const toProcess = incoming.slice(0, room);
      if (incoming.length > toProcess.length) {
        setInfoNotice(
          `Only ${toProcess.length} more image${toProcess.length === 1 ? "" : "s"} fit for this post.`
        );
      }

      setUploading(true);
      setUploadError(null);

      try {
        for (const file of toProcess) {
          const type = String(file.type || "").toLowerCase();
          if (!ACCEPT_MIMES.has(type)) {
            setUploadError("Choose JPEG, PNG, WebP, or GIF files.");
            break;
          }
          if (file.size > MAX_UPLOAD_BYTES) {
            setUploadError("An image is too large. Maximum size is 10MB per file.");
            break;
          }

          if (attachmentsRef.current.length >= MAX_MEDIA_IMAGES_PER_POST) break;

          const blobUrl = URL.createObjectURL(file);
          const uploadId = ++uploadSeqRef.current;
          const pendingRow = {
            kind: "image",
            url: "",
            __uploadId: uploadId,
            __localPreview: blobUrl,
            __uploading: true,
            __ready: false,
          };

          setAttachments((prev) => {
            if (prev.length >= MAX_MEDIA_IMAGES_PER_POST) {
              URL.revokeObjectURL(blobUrl);
              return prev;
            }
            const next = [...prev, pendingRow];
            attachmentsRef.current = next;
            return next;
          });

          try {
            const normalized = await uploadImageFile(file);
            if (!normalized || !normalized.url) {
              throw new Error("Upload succeeded but attachment data was incomplete.");
            }
            setAttachments((prev) => {
              const i = prev.findIndex((x) => x.__uploadId === uploadId);
              if (i < 0) {
                URL.revokeObjectURL(blobUrl);
                const without = prev.filter((x) => x.__uploadId !== uploadId);
                const merged = [...without, { ...normalized, __ready: true }];
                attachmentsRef.current = merged;
                return merged;
              }
              const next = [...prev];
              URL.revokeObjectURL(blobUrl);
              next[i] = { ...normalized, __ready: true };
              attachmentsRef.current = next;
              return next;
            });
          } catch (err) {
            setUploadError(getUploadErrorMessage(err));
            setAttachments((prev) => {
              const i = prev.findIndex((x) => x.__uploadId === uploadId);
              URL.revokeObjectURL(blobUrl);
              if (i < 0) {
                const next = prev.filter((x) => x.__uploadId !== uploadId);
                attachmentsRef.current = next;
                return next;
              }
              const next = prev.filter((_, j) => j !== i);
              attachmentsRef.current = next;
              return next;
            });
            break;
          }
        }
      } finally {
        setUploading(false);
      }
    },
    [uploadImageFile]
  );

  const processReplaceAtIndex = useCallback(
    async (index, file) => {
      if (composerTabRef.current !== "media") {
        setComposerTab("media");
      }
      if (!file || !Number.isFinite(Number(index)) || index < 0) return;

      const row = attachmentsRef.current[index];
      if (!row || !row.__ready) return;

      const type = String(file.type || "").toLowerCase();
      if (!ACCEPT_MIMES.has(type)) {
        setUploadError("Choose JPEG, PNG, WebP, or GIF files.");
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setUploadError("An image is too large. Maximum size is 10MB per file.");
        return;
      }

      const snapshot = { ...row };
      const blobUrl = URL.createObjectURL(file);
      const pendingRow = {
        kind: "image",
        url: "",
        __localPreview: blobUrl,
        __uploading: true,
        __ready: false,
      };

      setUploading(true);
      setUploadError(null);
      setAttachments((prev) => {
        const next = [...prev];
        if (next[index]) revokeLocalPreview(next[index]);
        next[index] = pendingRow;
        attachmentsRef.current = next;
        return next;
      });

      try {
        const normalized = await uploadImageFile(file);
        if (!normalized || !normalized.url) {
          throw new Error("Upload succeeded but attachment data was incomplete.");
        }
        setAttachments((prev) => {
          const next = [...prev];
          URL.revokeObjectURL(blobUrl);
          next[index] = { ...normalized, __ready: true };
          attachmentsRef.current = next;
          return next;
        });
      } catch (err) {
        setUploadError(getUploadErrorMessage(err));
        setAttachments((prev) => {
          const next = [...prev];
          URL.revokeObjectURL(blobUrl);
          next[index] = snapshot;
          attachmentsRef.current = next;
          return next;
        });
      } finally {
        setUploading(false);
      }
    },
    [uploadImageFile, revokeLocalPreview]
  );

  const onPickComposerImage = useCallback(
    async (e) => {
      const list = e.target?.files;
      if (!list?.length) return;
      const files = Array.from(list);
      e.target.value = "";
      await processFilesBatch(files);
    },
    [processFilesBatch]
  );

  const onReplaceFileInputChange = useCallback(
    async (e) => {
      const list = e.target?.files;
      if (!list?.length) return;
      const file = list[0];
      e.target.value = "";
      if (!file) return;
      await processReplaceAtIndex(replaceSlideIndexRef.current, file);
    },
    [processReplaceAtIndex]
  );

  const removeAtComposerIndex = useCallback(
    (index) => {
      setAttachments((prev) => {
        const row = prev[index];
        if (row) revokeLocalPreview(row);
        const next = prev.filter((_, i) => i !== index);
        attachmentsRef.current = next;
        return next;
      });
      setUploadError(null);
    },
    [revokeLocalPreview]
  );

  const openAddPicker = useCallback(() => attachmentInputRef.current?.click(), []);
  const openReplacePicker = useCallback(() => {
    replaceSlideIndexRef.current = mediaSlideIndex;
    replaceFileInputRef.current?.click();
  }, [mediaSlideIndex]);

  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setDragActive(false);
      if (createPostBusy || uploading) return;
      const dt = e.dataTransfer?.files;
      if (!dt?.length) return;
      const images = Array.from(dt).filter((f) =>
        ACCEPT_MIMES.has(String(f.type || "").toLowerCase())
      );
      if (!images.length) {
        setUploadError("Drop JPEG, PNG, WebP, or GIF images.");
        return;
      }
      await processFilesBatch(images);
    },
    [createPostBusy, uploading, processFilesBatch]
  );

  const onTabSelect = useCallback(
    (tab) => {
      if (tab.disabled) {
        setInfoNotice("That post type is not available yet.");
        return;
      }
      setComposerTab(tab.id);
      setCreatePostErr("");
    },
    []
  );

  const submitPost = useCallback(async () => {
    if (!canPublish) return;
    if (!pickViewerId(user)) {
      setCreatePostErr("Sign in to publish.");
      return;
    }

    const attachmentRows = buildV2AttachmentPayloadFromComposer(attachments);
    if (isMediaTab && attachmentRows.length === 0) {
      setCreatePostErr("Add at least one image to publish.");
      return;
    }

    const payload = buildV2CreatePostPayload({
      surface,
      isMediaTab,
      title: postTitle,
      body: postBody,
      topicId: "other",
      tags: [],
      clubId: effectivePostClubId,
      attachmentRows,
    });

    setCreatePostBusy(true);
    setCreatePostErr("");
    try {
      const result = await createPost(payload);
      await onSuccess?.(result);
      onClose();
    } catch (e) {
      setCreatePostErr(e?.message || "Could not publish post.");
    } finally {
      setCreatePostBusy(false);
    }
  }, [
    canPublish,
    user,
    attachments,
    isMediaTab,
    surface,
    postTitle,
    postBody,
    effectivePostClubId,
    onSuccess,
    onClose,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== "Enter") return;
      if (!canPublish) return;
      e.preventDefault();
      submitPost();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, canPublish, submitPost]);

  const mediaSlides = attachments.map((a) => ({ src: composerSlideSrc(a) }));
  const canAddMoreImages =
    attachments.length < MAX_MEDIA_IMAGES_PER_POST &&
    !createPostBusy &&
    !uploading &&
    !attachments.some((a) => a.__uploading);
  const showBodyLength =
    !isMediaTab && (!contentLengthOk || storedContent.length >= MAX_CONTENT_CHARS * 0.85);
  const busyShell = createPostBusy;

  const displayTitle = headlineTitle || "Create post";

  const contextSubtitleTrim =
    contextSubtitle != null && String(contextSubtitle).trim() !== ""
      ? String(contextSubtitle).trim()
      : null;

  const contextTargetLabel =
    surface === "community" && effectivePostClubId == null
      ? "Community"
      : clubPickerOptionsEffective.find((c) => Number(c.id) === effectivePostClubId)?.name?.trim() ||
        club?.name?.trim() ||
        "this club";

  if (!isOpen) return null;

  if (surface === "club" && !club) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center px-3 py-6 sm:px-5 sm:py-8">
      <button
        type="button"
        className="absolute inset-0 bg-black/[0.92] transition-opacity"
        onClick={tryClose}
        aria-label="Close create post"
      />
      <div
        className={cn(
          "relative z-[10001] flex w-full max-w-xl flex-col overflow-hidden",
          "rounded-2xl border border-white/12 !bg-[#0b101a] shadow-[0_24px_80px_rgba(0,0,0,0.65)] ring-1 ring-white/[0.06]",
          "!backdrop-blur-none max-h-[min(92vh,736px)] min-h-0 w-full min-w-0 max-w-full box-border"
        )}
        role="dialog"
        aria-labelledby="club-feed-composer-title"
        aria-describedby="club-feed-composer-context"
        aria-modal="true"
      >
        <div className={cn(clubPostCardHeaderClass, "shrink-0 pb-3 pt-4 sm:pt-5")}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2
                id="club-feed-composer-title"
                className="text-lg font-semibold tracking-tight text-white sm:text-xl"
              >
                {displayTitle}
              </h2>
              <p id="club-feed-composer-context" className="mt-1 text-[13px] text-gray-500">
                {contextSubtitleTrim ? (
                  contextSubtitleTrim
                ) : (
                  <>
                    Post in <span className="text-gray-400">{contextTargetLabel}</span>
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              disabled={createPostBusy}
              onClick={tryClose}
              className={cn(clubPostOptionsTriggerClass(false), "disabled:opacity-40")}
              aria-label="Close"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <ClubFeedComposerTabList activeId={composerTab} onSelect={onTabSelect} />

        <div
          className={cn(
            clubPostCardBodyClass,
            "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain !py-4 sm:!py-5 md:!py-5"
          )}
        >
          {/* Keep file inputs mounted across tab switches so the native picker can finish without losing onChange. */}
          <input
            ref={attachmentInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            onChange={onPickComposerImage}
            className="hidden"
            tabIndex={-1}
            aria-hidden
          />
          <input
            ref={replaceFileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            onChange={onReplaceFileInputChange}
            className="hidden"
            tabIndex={-1}
            aria-hidden
          />

          <div className="flex min-h-0 flex-1 flex-col gap-5">
            {infoNotice ? (
              <div className={alertInfoClass} role="status">
                {infoNotice}
              </div>
            ) : null}

            <div>
              <label htmlFor="club-feed-post-title-input" className="sr-only">
                Title
              </label>
              <input
                ref={titleInputRef}
                id="club-feed-post-title-input"
                type="text"
                value={postTitle}
                onChange={(e) => {
                  setPostTitle(e.target.value);
                  setCreatePostErr("");
                }}
                disabled={busyShell}
                maxLength={MAX_TITLE_CHARS}
                placeholder="Title"
                className={cn(fieldShellClass, "font-medium tracking-tight")}
                autoComplete="off"
              />
              <div className="mt-1 flex justify-end text-[11px] tabular-nums text-gray-600">
                {titleLen.toLocaleString()}/{MAX_TITLE_CHARS.toLocaleString()}
              </div>
            </div>

            {!isMediaTab ? (
              <div>
                <label htmlFor="club-feed-post-body" className="sr-only">
                  Body text
                </label>
                <textarea
                  ref={bodyRef}
                  id="club-feed-post-body"
                  value={postBody}
                  onChange={(e) => {
                    setPostBody(e.target.value);
                    setCreatePostErr("");
                  }}
                  disabled={busyShell}
                  rows={BODY_ROWS}
                  placeholder="Write your post (optional)"
                  className={cn(
                    fieldShellClass,
                    BODY_MIN_PX,
                    "resize-y leading-relaxed",
                    clubPostCardBodyTextClass
                  )}
                />
                {showBodyLength ? (
                  <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600">
                    <span>
                      <span className="tabular-nums text-gray-500">
                        {storedContent.length.toLocaleString()}
                      </span>
                      {" / "}
                      {MAX_CONTENT_CHARS.toLocaleString()}
                    </span>
                    {!contentLengthOk ? (
                      <span className="text-amber-200/55">Over limit</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div>
                <ClubFeedComposerMediaPanel
                  slides={mediaSlides}
                  slideIndex={mediaSlideIndex}
                  onSlideChange={setMediaSlideIndex}
                  canAddMore={canAddMoreImages}
                  uploading={uploading}
                  dragActive={dragActive}
                  busy={busyShell}
                  onAdd={openAddPicker}
                  onEditCurrent={openReplacePicker}
                  canReplaceCurrent={Boolean(attachments[mediaSlideIndex]?.__ready)}
                  onRemoveCurrent={() => removeAtComposerIndex(mediaSlideIndex)}
                  onDragEnter={onDragEnter}
                  onDragLeave={onDragLeave}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                />
              </div>
            )}

            {((surface === "community" && clubPickerOptionsEffective.length > 0) ||
              (surface === "club" && clubPickerOptionsEffective.length > 0)) && (
              <div>
                <div className="mb-2 text-xs text-gray-400">
                  {surface === "community" ? "Post to" : "Post in"}
                </div>
                <select
                  value={selectedTargetClubId != null ? String(selectedTargetClubId) : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTargetClubId(v === "" ? null : Number(v));
                    setCreatePostErr("");
                  }}
                  disabled={busyShell}
                  className={cn(fieldShellClass, "cursor-pointer")}
                  aria-label={surface === "community" ? "Post destination" : "Club feed destination"}
                >
                  {surface === "community" ? (
                    <option value="" className="bg-[#0b101a]">
                      Community
                    </option>
                  ) : null}
                  {clubPickerOptionsEffective.map((c) => (
                    <option key={c.id} value={String(c.id)} className="bg-[#0b101a]">
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isMediaTab && uploadError ? (
              <div className={alertErrorClass} role="alert" aria-live="polite">
                <p className="text-rose-100/80">{uploadError}</p>
                <button
                  type="button"
                  className="mt-2 text-[12px] font-medium text-gray-400 underline decoration-white/15 underline-offset-2 hover:text-gray-300"
                  onClick={() => {
                    setUploadError(null);
                    openAddPicker();
                  }}
                >
                  Try another file
                </button>
              </div>
            ) : null}

            {createPostErr ? (
              <div
                className={cn(alertErrorClass, "font-medium text-rose-100/90")}
                role="alert"
                aria-live="assertive"
              >
                {createPostErr}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-white/10 bg-[#080d14] px-3 py-3 sm:px-5 md:px-6">
          <div className="min-w-0 flex-1 text-[12px] leading-snug text-gray-600">
            {footerHint ? <p className="pr-2">{footerHint}</p> : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={createPostBusy}
              onClick={tryClose}
              className="rounded-lg px-3 py-2 text-[13px] font-medium text-gray-500 transition hover:bg-white/[0.04] hover:text-gray-300 disabled:opacity-35"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canPublish}
              onClick={submitPost}
              className={cn(
                clubFeedActionBase,
                "!h-9 !min-h-0 rounded-lg !px-4 !text-[13px] border-white/12 bg-[#1a222e] text-white/88 hover:bg-[#242e3d]",
                "disabled:cursor-not-allowed disabled:border-white/10 disabled:bg-[#121820] disabled:text-white/28 disabled:shadow-none"
              )}
            >
              {createPostBusy ? (
                <>
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-white/55" aria-hidden />
                  Posting…
                </>
              ) : (
                "Post"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
